// routes
import { PATH_DASHBOARD } from '@raas/shared/src/routes/paths';
import Iconify from '@raas/shared/src/components/minimals/iconify';
import { ICON_NAME } from '@raas/shared/src/utils/icons';

const icon = (name: string) => <Iconify icon={name} />;

const ICONS = {
  downloads: icon(ICON_NAME.downloads),
  gear: icon(ICON_NAME.gear),
  eye: icon(ICON_NAME.eye),
  tags: icon(ICON_NAME.tags),
  alerts: icon(ICON_NAME.alerts),
  reports: icon(ICON_NAME.reports),
  import: icon(ICON_NAME.import),
  tag: icon(ICON_NAME.tag),
  export: icon(ICON_NAME.export),
  requirement: icon(ICON_NAME.requirement),
  input: icon(ICON_NAME.input),
  legal: icon(ICON_NAME.legal),
  legalRequirement: icon(ICON_NAME.legalRequirement),
  radar: icon(ICON_NAME.radar),
  delta: icon(ICON_NAME.delta),
  translation: icon(ICON_NAME.translation),
  admin: icon(ICON_NAME.admin),
  integration: icon(ICON_NAME.integration),
  attribute: icon(ICON_NAME.attribute),
  regulationAccess: icon(ICON_NAME.regulationAccess),
  bundling: icon(ICON_NAME.bundling),
  organization: icon(ICON_NAME.organization),
};

type NavItem = {
  title: string;
  path: string;
  icon: JSX.Element;
  versionDependent: boolean;
  permissionRequired?: string;
  translationDependent?: boolean;
  disabled?: boolean;
};

type NavSection = {
  subheader: string;
  items: NavItem[];
};

const navConfig: NavSection[] = [
  {
    subheader: 'MENU',
    // the 'title' is actually a key that is then looked up in translation file
    items: [
      {
        title: 'legalRadar.header',
        icon: ICONS.radar,
        path: PATH_DASHBOARD.radar.root,
        versionDependent: false,
      },
      {
        title: 'legalProvisions.header',
        icon: ICONS.legal,
        path: PATH_DASHBOARD.legalProvision.root,
        versionDependent: false,
      },
      {
        title: 'bundlings.page.header',
        icon: ICONS.bundling,
        path: PATH_DASHBOARD.bundling.list,
        versionDependent: true,
      },
      {
        title: 'regulations.page.header',
        icon: ICONS.legal,
        path: PATH_DASHBOARD.regulation.root,
        versionDependent: false,
      },
      {
        title: 'legalRequirements.header',
        icon: ICONS.legalRequirement,
        path: PATH_DASHBOARD.legalRequirement.root,
        versionDependent: true,
        permissionRequired: 'LEGAL_REQUIREMENTS',
      },
      {
        title: 'technicalRequirements.header',
        icon: ICONS.requirement,
        path: PATH_DASHBOARD.technicalRequirement.root,
        versionDependent: true,
      },
      {
        title: 'properties.header',
        icon: ICONS.gear,
        path: PATH_DASHBOARD.property.root,
        versionDependent: true,
      },
      {
        title: 'inputs.header',
        icon: ICONS.input,
        path: PATH_DASHBOARD.input.root,
        versionDependent: true,
      },
      {
        title: 'legalProvisions.translationPage.header',
        path: PATH_DASHBOARD.translation,
        icon: ICONS.translation,
        versionDependent: true,
        translationDependent: true,
      },
      {
        title: 'alerts.header',
        icon: ICONS.alerts,
        path: PATH_DASHBOARD.alerts.list,
        versionDependent: false,
      },
    ],
  },
  {
    subheader: 'UPDATES',
    items: [
      {
        title: 'Delta',
        path: PATH_DASHBOARD.delta.legalProvisions(),
        icon: ICONS.delta,
        versionDependent: false,
      },
    ],
  },
  {
    subheader: 'DATA MANAGEMENT',
    items: [
      {
        title: 'tagGroups.header',
        icon: ICONS.tags,
        path: PATH_DASHBOARD.tagGroup.root,
        versionDependent: true,
      },
      {
        title: 'tags.header',
        icon: ICONS.tag,
        path: PATH_DASHBOARD.tag.root,
        versionDependent: true,
      },
      {
        title: 'attributes.page.header',
        icon: ICONS.attribute,
        path: PATH_DASHBOARD.attributes.root,
        versionDependent: true,
      },
    ],
  },
  {
    subheader: 'reports.subHeader',
    items: [
      {
        title: 'import.header',
        path: PATH_DASHBOARD.import,
        icon: ICONS.import,
        versionDependent: false,
      },
      {
        title: 'reports.header',
        path: PATH_DASHBOARD.reports.list,
        icon: ICONS.reports,
        versionDependent: false,
      },
    ],
  },
  {
    subheader: 'EXPORT',
    items: [
      {
        title: 'exports.header',
        path: PATH_DASHBOARD.exports.list,
        icon: ICONS.export,
        versionDependent: false,
      },
      {
        title: 'integration.header',
        path: PATH_DASHBOARD.integration.root,
        icon: ICONS.integration,
        versionDependent: false,
      },
    ],
  },
  {
    subheader: 'SETTINGS',
    items: [
      {
        title: 'admins.page.header',
        path: PATH_DASHBOARD.admins.root,
        icon: ICONS.admin,
        versionDependent: false,
      },
      {
        title: 'organization.page.header',
        path: PATH_DASHBOARD.organization.root,
        icon: ICONS.organization,
        versionDependent: false,
      },
      {
        title: 'integration.settings.header',
        path: PATH_DASHBOARD.integration.settings,
        icon: ICONS.integration,
        versionDependent: false,
      },
    ],
  },
];

interface IGetNavPros {
  hasVersion: boolean;
  hasTranslations: boolean;
}

function getNavConfig({ hasVersion, hasTranslations }: IGetNavPros): any {
  if (hasVersion && hasTranslations) {
    return navConfig;
  }

  if (hasVersion && !hasTranslations) {
    return navConfig.map((group) => {
      const items = group.items.filter((i) => !i?.translationDependent);
      return { ...group, items };
    });
  }

  return navConfig.map((group) => {
    const items = group.items.filter((i) => !i.versionDependent && !i?.translationDependent);
    return { ...group, items };
  });
}

export default getNavConfig;
